import { useLocation, Navigate, Link } from "react-router-dom";
import Seo from "../components/Seo";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/Breadcrumbs";
import { PANNES_GEO, findPanneGeo } from "../data/pannes-geo";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Layout";
import { siteUrl } from "../lib/siteUrl";
import InterPivotLinks from "../components/sections/InterPivotLinks";
import ImageGallery from "../components/sections/ImageGallery";
import { APPLIANCE_IMAGES } from "../data/images";

/**
 * Geo-localized symptom pages. URL : /{slug-panne-ville}
 * High commercial intent (symptom + zone). Sister to PannePage (/panne/{slug}).
 */

const URGENCE_MAP = {
    "très élevée": { label: "URGENCE MAXIMALE", color: "text-red-800 bg-red-100 border-red-300", icon: "fa-bell" },
    "élevée": { label: "Urgence ÉLEVÉE", color: "text-red-700 bg-red-50 border-red-200", icon: "fa-triangle-exclamation" },
    "moyenne": { label: "Urgence modérée", color: "text-amber-800 bg-amber-50 border-amber-200", icon: "fa-clock" },
    "faible": { label: "Urgence faible", color: "text-emerald-800 bg-emerald-50 border-emerald-200", icon: "fa-circle-info" },
};

function stripMarkdown(s) { return (s || "").replace(/\*\*([^*]+)\*\*/g, "$1"); }

function buildFaqs(p) {
    const appLow = p.applianceLabel.toLowerCase();
    return [
        {
            q: `Combien coûte la réparation d'un ${appLow} à ${p.zoneLabel} ?`,
            a: `Entre ${p.coutEstime} en 2026, déplacement et diagnostic inclus. Devis ferme avant intervention, garantie 18 mois pièces et main-d'œuvre.`,
        },
        {
            q: `Quel est votre délai d'intervention à ${p.zoneLabel} ?`,
            a: `${p.delai} dans la majorité des cas, intervention le jour même selon disponibilités. ${p.zoneLabel} fait partie de nos zones prioritaires.`,
        },
        {
            q: `Intervenez-vous le samedi à ${p.zoneLabel} ?`,
            a: `Oui, intervention 6j/7 (lundi-samedi) sur ${p.zoneLabel}. Urgences dimanche selon planning.`,
        },
        {
            q: `Réparez-vous toutes les marques à ${p.zoneLabel} ?`,
            a: `Oui : Bosch, Siemens, Miele, Whirlpool, Samsung, LG, Electrolux, Smeg, Gaggenau, Liebherr et toutes marques courantes. Techniciens formés multi-marques.`,
        },
    ];
}

function buildJsonLd({ p, url, crumbs, faqs }) {
    return {
        "@context": "https://schema.org",
        "@graph": [
            breadcrumbJsonLd(crumbs),
            {
                "@type": "WebPage",
                "@id": `${url}#webpage`,
                "url": url,
                "name": p.h1,
                "inLanguage": "fr-FR",
                "speakable": {
                    "@type": "SpeakableSpecification",
                    "cssSelector": [".speakable-headline", ".speakable-intro", ".speakable-voice-faq"],
                },
            },
            {
                "@type": "Article",
                "headline": p.h1,
                "description": p.description,
                "datePublished": "2026-02-01",
                "dateModified": "2026-02-15",
                "author": { "@type": "Organization", "name": "Electrocase", "url": siteUrl("/") },
                "publisher": { "@id": siteUrl("/#business") },
                "mainEntityOfPage": url,
                "image": siteUrl("/og-image.jpg"),
                "inLanguage": "fr-FR",
            },
            {
                "@type": "Service",
                "serviceType": `Réparation ${p.applianceLabel} à domicile`,
                "areaServed": { "@type": "Place", "name": p.zoneLabel },
                "provider": { "@id": siteUrl("/#business") },
                "offers": {
                    "@type": "Offer",
                    "priceCurrency": "EUR",
                    "priceSpecification": {
                        "@type": "PriceSpecification",
                        "price": p.coutEstime,
                        "priceCurrency": "EUR",
                    },
                },
            },
            {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                    "@type": "Question",
                    "name": f.q,
                    "acceptedAnswer": { "@type": "Answer", "text": stripMarkdown(f.a) },
                })),
            },
        ],
    };
}

function CausesSection({ causes }) {
    return (
        <section className="bg-white border-y border-[var(--line)]">
            <div className="max-w-5xl mx-auto px-5 lg:px-10 py-14">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-2xl lg:text-3xl font-extrabold">Causes possibles & tarifs 2026</h2>
                <div className="mt-8 space-y-4">
                    {causes.map((c, i) => (
                        <article key={`cause-${i}`} className="card-soft p-5 lg:p-6" data-testid={`geo-cause-${i}`}>
                            <div className="flex items-start gap-4">
                                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[var(--navy)] text-[var(--cream)] font-bold heading shrink-0">
                                    {i + 1}
                                </span>
                                <div className="flex-1">
                                    <h3 className="heading text-lg font-bold text-[var(--navy)]">{c.t}</h3>
                                    <p className="mt-2 text-[var(--ink-soft)] leading-relaxed">{c.d}</p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function DiySection({ diy }) {
    return (
        <section className="max-w-5xl mx-auto px-5 lg:px-10 py-14">
            <div className="divider-amber mb-5" />
            <h2 className="heading text-2xl lg:text-3xl font-extrabold">Vérifications avant d&apos;appeler</h2>
            <ol className="mt-8 space-y-3">
                {diy.map((d, i) => (
                    <li key={`diy-${i}`} className="flex items-start gap-4 bg-[var(--paper)] border border-[var(--line)] rounded-xl p-4">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--amber)] text-[var(--navy)] font-bold heading text-sm shrink-0">
                            {i + 1}
                        </span>
                        <p className="text-[var(--ink-soft)] leading-relaxed pt-1">{d}</p>
                    </li>
                ))}
            </ol>
        </section>
    );
}

export default function PanneGeoPage() {
    const location = useLocation();
    const slug = location.pathname.replace(/^\//, "");
    const p = findPanneGeo(slug);
    if (!p) return <Navigate to="/" replace />;

    const url = siteUrl(`/${p.slug}`);
    const urgInfo = URGENCE_MAP[p.urgence] || URGENCE_MAP.moyenne;
    const faqs = buildFaqs(p);

    const crumbs = [
        { name: "Accueil", to: "/" },
        { name: p.zoneLabel, to: p.cityHubLink },
        { name: p.h1.split(" — ")[0] },
    ];
    const jsonLd = buildJsonLd({ p, url, crumbs, faqs });

    return (
        <>
            <Seo title={p.title} description={p.description} keywords={p.keywords} canonical={url} jsonLd={jsonLd} />

            <section className="bg-[var(--paper)] border-b border-[var(--line)]">
                <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
                    <Breadcrumbs items={crumbs} />
                    <div className="mt-6">
                        <span className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border ${urgInfo.color}`}>
                            <i className={`fa-solid ${urgInfo.icon}`} /> {urgInfo.label} · {p.zoneLabel}
                        </span>
                        <h1 className="heading text-3xl lg:text-5xl font-extrabold mt-5 leading-[1.05] speakable-headline">{p.h1}</h1>
                        <p className="mt-5 text-lg text-[var(--ink-soft)] max-w-3xl speakable-intro">{p.intro}</p>
                        <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-3xl">
                            <div className="card-soft p-4">
                                <p className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)]/70 font-semibold">Zone</p>
                                <p className="heading font-bold text-[var(--navy)] mt-1 flex items-center gap-2">
                                    <i className="fa-solid fa-location-dot text-[var(--amber)]" /> {p.zoneLabel}
                                </p>
                                <p className="text-[10px] text-[var(--ink-soft)]/70 mt-1">{p.zoneIntro}</p>
                            </div>
                            <div className="card-soft p-4">
                                <p className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)]/70 font-semibold">Tarif 2026</p>
                                <p className="heading font-bold text-[var(--navy)] mt-1">{p.coutEstime}</p>
                            </div>
                            <div className="card-soft p-4">
                                <p className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)]/70 font-semibold">Délai</p>
                                <p className="heading font-bold text-[var(--navy)] mt-1">{p.delai}</p>
                            </div>
                        </div>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <a href={PHONE_TEL} className="btn-amber inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold" data-testid="geo-call-btn">
                                <i className="fa-solid fa-phone-volume" /> Appeler {PHONE_DISPLAY}
                            </a>
                            <Link to="/contact" className="btn-outline-navy inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold">
                                Devis gratuit
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTEXTE LOCAL */}
            <section className="max-w-5xl mx-auto px-5 lg:px-10 py-12">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-2xl lg:text-3xl font-extrabold">Spécificités {p.zoneLabel}</h2>
                <p className="mt-4 text-[var(--ink-soft)] leading-relaxed text-lg">{p.localContext}</p>
            </section>

            <CausesSection causes={p.causes} />
            <DiySection diy={p.diy} />

            {/* CTA milieu */}
            <section className="max-w-5xl mx-auto px-5 lg:px-10 pb-14">
                <div className="rounded-3xl bg-[var(--navy)] text-[var(--cream)] p-8 lg:p-12 grid lg:grid-cols-2 gap-6 items-center relative overflow-hidden grain">
                    <div className="relative">
                        <p className="text-xs uppercase tracking-[0.2em] text-[var(--amber)] font-semibold">Intervention {p.zoneLabel}</p>
                        <h2 className="heading text-2xl lg:text-3xl font-extrabold text-[var(--cream)] mt-3">Diagnostic + réparation à domicile, garanti 18 mois.</h2>
                    </div>
                    <div className="relative flex flex-wrap gap-3 lg:justify-end">
                        <a href={PHONE_TEL} className="btn-amber inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-base font-bold">
                            <i className="fa-solid fa-phone-volume" /> {PHONE_DISPLAY}
                        </a>
                        <Link to={p.cityHubLink} className="bg-white text-[var(--navy)] inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-base font-semibold">
                            Hub {p.zoneLabel}
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="bg-[var(--paper)] border-y border-[var(--line)]">
                <div className="max-w-4xl mx-auto px-5 lg:px-10 py-14">
                    <div className="divider-amber mb-5" />
                    <h2 className="heading text-2xl lg:text-3xl font-extrabold">Questions fréquentes</h2>
                    <div className="mt-8 divide-y divide-[var(--line)] border-y border-[var(--line)] bg-white rounded-2xl">
                        {faqs.map((f, i) => (
                            <details key={f.q} className="group p-5" data-testid={`geo-faq-${i}`}>
                                <summary className="flex items-start justify-between cursor-pointer list-none gap-4">
                                    <span className="heading font-bold text-[var(--navy)]">{f.q}</span>
                                    <i className="fa-solid fa-plus text-[var(--amber)] mt-1 shrink-0 group-open:rotate-45 transition-transform" />
                                </summary>
                                <p className="mt-3 text-[var(--ink-soft)] leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* MAILLAGE */}
            <section className="max-w-5xl mx-auto px-5 lg:px-10 py-14">
                <div className="divider-amber mb-5" />
                <h2 className="heading text-xl lg:text-2xl font-extrabold">Pour aller plus loin</h2>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <Link to={`/panne/${p.relatedPanne}`} className="card-soft p-5 block group" data-testid="geo-related-panne">
                        <i className={`${p.icon} text-[var(--amber)] text-lg`} />
                        <p className="heading font-bold text-sm mt-3 group-hover:text-[var(--navy)]">Guide complet : {p.applianceLabel}</p>
                        <p className="text-xs text-[var(--ink-soft)] mt-2 line-clamp-2">Causes, diagnostic, prix : la page de référence pour ce symptôme.</p>
                    </Link>
                    <Link to={`/${p.applianceServiceSlug}`} className="card-soft p-5 block group" data-testid="geo-related-service">
                        <i className="fa-solid fa-screwdriver-wrench text-[var(--amber)] text-lg" />
                        <p className="heading font-bold text-sm mt-3 group-hover:text-[var(--navy)]">Réparation {p.applianceLabel.toLowerCase()} {p.zoneLabel}</p>
                        <p className="text-xs text-[var(--ink-soft)] mt-2 line-clamp-2">Toutes les pannes, tarifs, marques et zones d&apos;intervention.</p>
                    </Link>
                </div>
            </section>

            <ImageGallery
                title={`${p.applianceLabel} ${p.zoneLabel} — En images`}
                images={APPLIANCE_IMAGES[p.appliance] || []}
            />

            <InterPivotLinks title={`Intervention dans votre zone — ${p.zoneLabel}`} />
        </>
    );
}

export function allPanneGeoPaths() {
    return PANNES_GEO.map((p) => `/${p.slug}`);
}
